import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AiOutlinePlus } from "react-icons/ai";
import { IoSearchOutline } from "react-icons/io5";
import "./new_MasterList.css";

export default function MasterListPage({
  addLabel,
  addPath,
  className = "",
  columns,
  description,
  emptyMessage,
  error,
  headerToolbarContent,
  isLoading,
  onRetry,
  rows,
  searchPlaceholder,
  searchPredicate,
  toolbarContent,
  title,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const filteredRows = useMemo(
    () => rows.filter((row) => searchPredicate(row, searchTerm)),
    [rows, searchPredicate, searchTerm]
  );

  useEffect(() => {
    if (page > 1 && (page - 1) * pageSize >= filteredRows.length) {
      setPage(1);
    }
  }, [filteredRows.length, page, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const paginatedRows = filteredRows.slice(startIndex, startIndex + pageSize);

  return (
    <div className={`masters-card ${className}`.trim()}>
      <div className="masters-card__header masters-card__header--list">
        <div className="masters-card__header-main">
          <h1 className="masters-card__title">{title}</h1>
          {description ? (
            <p className="masters-card__description">{description}</p>
          ) : null}
        </div>

        <div className="masters-list__toolbar masters-list__toolbar--header">
          <div className="masters-search">
            <IoSearchOutline className="masters-search__icon" />
            <input
              className="masters-search__input"
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setPage(1);
              }}
              placeholder={searchPlaceholder}
              type="text"
              value={searchTerm}
            />
          </div>

          {headerToolbarContent ? (
            <div className="masters-list__toolbar-inline-content">
              {headerToolbarContent}
            </div>
          ) : null}

          {addPath ? (
            <div className="masters-card__actions">
              <Link className="masters-link-btn" to={addPath}>
                <AiOutlinePlus />
                {addLabel}
              </Link>
            </div>
          ) : null}
        </div>
      </div>

      {toolbarContent ? (
        <div className="masters-list__toolbar-content">{toolbarContent}</div>
      ) : null}

      {isLoading ? (
        <div className="masters-state">
          <div className="masters-state__loader" />
          <h2 className="masters-state__title">Loading data</h2>
          <p className="masters-state__text">
            Please wait while the module data is being fetched.
          </p>
        </div>
      ) : error ? (
        <div className="masters-state">
          <h2 className="masters-state__title">Unable to load data</h2>
          <p className="masters-state__text">{error}</p>
          <div style={{ marginTop: "16px" }}>
            <button className="masters-btn" onClick={onRetry} type="button">
              Retry
            </button>
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="masters-state">
          <h2 className="masters-state__title">No records found</h2>
          <p className="masters-state__text">{emptyMessage}</p>
        </div>
      ) : (
        <>
          <div className="masters-table-wrap">
            <div className="masters-table-scroll">
              <table className="masters-table">
                <thead>
                  <tr>
                    {columns.map((column) => (
                      <th key={column.key}>{column.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.length > 0 ? (
                    paginatedRows.map((row, index) => {
                      const rowNumber = startIndex + index + 1;

                      return (
                        <tr key={row.id || rowNumber}>
                          {columns.map((column) => (
                            <td key={column.key}>
                              {column.render
                                ? column.render(row, index, rowNumber)
                                : row[column.key] || "-"}
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={columns.length}>
                        <div className="masters-state">
                          <h2 className="masters-state__title">
                            No matching records
                          </h2>
                          <p className="masters-state__text">
                            Try changing the search text.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="masters-pagination">
            <div>
              Showing {paginatedRows.length === 0 ? 0 : startIndex + 1} to{" "}
              {Math.min(startIndex + pageSize, filteredRows.length)} of{" "}
              {filteredRows.length}
            </div>

            <div className="masters-pagination__actions">
              <select
                className="masters-form__select masters-pagination__select"
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(1);
                }}
                value={pageSize}
              >
                <option value={10}>10 / page</option>
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
              </select>
              <button
                className="masters-btn masters-btn--secondary"
                disabled={safePage === 1}
                onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
                type="button"
              >
                Previous
              </button>
              <button
                className="masters-btn"
                disabled={safePage >= totalPages}
                onClick={() =>
                  setPage((currentPage) =>
                    Math.min(totalPages, currentPage + 1)
                  )
                }
                type="button"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
